/**
 * CalendarViewArea
 *
 * Renders the active calendar view (month, week, day, or agenda) based on the
 * current view state. Also displays loading indicators and error states.
 */

import { Loader2 } from 'lucide-react';
import type { CalendarView, CalendarSettings, EventInstance, WeatherDay } from '../types';

import { MonthView } from './MonthView';
import { WeekView } from './WeekView';
import { DayView } from './DayView';
import { AgendaView } from './AgendaView';

export interface CalendarViewAreaProps {
  view: CalendarView;
  currentDate: Date;
  visibleEvents: EventInstance[];
  settings: CalendarSettings;
  loading: boolean;
  error: string | null;

  // Event interactions
  onClickDate: (date: Date) => void;
  onSelectDays: (from: Date, to: Date) => void;
  onShowMore: (date: Date) => void;
  onClickEvent: (event: EventInstance) => void;
  onSelectRange: (start: Date, end: Date) => void;

  // Resize
  resizingEventId: string | null;
  resizeHeight: number | null;
  onResizeStart: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  draggingEventId: string | null;
  dragOffset: { x: number; y: number } | null;
  onEventDragStart: (eventId: string, e: React.MouseEvent) => void;
  /** Month view drags days only, so it gets its own gesture. */
  monthDraggingEventId: string | null;
  onMonthEventDragStart: (eventId: string, e: React.MouseEvent) => void;

  // Weather
  weather: Map<string, WeatherDay>;

  // Derived settings
  hourHeightPx: number;
}

export function CalendarViewArea({
  view,
  currentDate,
  visibleEvents,
  settings,
  loading,
  error,
  onClickDate,
  onSelectDays,
  onShowMore,
  onClickEvent,
  onSelectRange,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  draggingEventId,
  dragOffset,
  onEventDragStart,
  monthDraggingEventId,
  onMonthEventDragStart,
  weather,
  hourHeightPx,
}: CalendarViewAreaProps) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      {loading && (
        <div className="absolute top-2 right-2 z-10">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && !loading && (
        <div className="flex-1 flex items-center justify-center text-destructive">
          <p>{error}</p>
        </div>
      )}

      {!error && view === 'month' && (
        <MonthView
          currentDate={currentDate}
          events={visibleEvents}
          weekStartDay={settings.weekStartDay}
          onClickDate={onClickDate}
          onSelectDays={onSelectDays}
          onShowMore={onShowMore}
          onClickEvent={onClickEvent}
          showWeekends={settings.showWeekends}
          monthViewMaxEvents={settings.monthViewMaxEvents}
          showEventTimesInMonthView={settings.showEventTimesInMonthView}
          showWeekNumbers={settings.showWeekNumbers}
          dimPastEvents={settings.dimPastEvents}
          eventColorPriority={settings.eventColorPriority}
          onEventDragStart={onMonthEventDragStart}
          draggingEventId={monthDraggingEventId}
        />
      )}

      {!error && view === 'week' && (
        <WeekView
          currentDate={currentDate}
          events={visibleEvents}
          weekStartDay={settings.weekStartDay}
          workingHoursStart={settings.workingHoursStart}
          timeFormat={settings.timeFormat}
          onSelectRange={onSelectRange}
          onClickEvent={onClickEvent}
          resizingEventId={resizingEventId}
          resizeHeight={resizeHeight}
          onResizeStart={onResizeStart}
          draggingEventId={draggingEventId}
          dragOffset={dragOffset}
          onEventDragStart={onEventDragStart}
          weather={weather}
          showWeekends={settings.showWeekends}
          showWeekNumbers={settings.showWeekNumbers}
          temperatureUnit={settings.temperatureUnit}
          hourHeightPx={hourHeightPx}
          weekViewAnchor={settings.weekViewAnchor}
          dimPastEvents={settings.dimPastEvents}
          eventColorPriority={settings.eventColorPriority}
          showConflictWarnings={settings.showConflictWarnings}
          minBreakBetweenEvents={settings.minBreakBetweenEvents}
          snapMinutes={settings.snapIntervalMinutes}
          defaultDurationMinutes={settings.defaultEventDuration}
        />
      )}

      {!error && view === 'day' && (
        <DayView
          currentDate={currentDate}
          events={visibleEvents}
          workingHoursStart={settings.workingHoursStart}
          timeFormat={settings.timeFormat}
          onSelectRange={onSelectRange}
          onClickEvent={onClickEvent}
          resizingEventId={resizingEventId}
          resizeHeight={resizeHeight}
          onResizeStart={onResizeStart}
          draggingEventId={draggingEventId}
          dragOffset={dragOffset}
          onEventDragStart={onEventDragStart}
          weather={weather}
          temperatureUnit={settings.temperatureUnit}
          hourHeightPx={hourHeightPx}
          dimPastEvents={settings.dimPastEvents}
          eventColorPriority={settings.eventColorPriority}
          showConflictWarnings={settings.showConflictWarnings}
          minBreakBetweenEvents={settings.minBreakBetweenEvents}
          snapMinutes={settings.snapIntervalMinutes}
          defaultDurationMinutes={settings.defaultEventDuration}
        />
      )}

      {!error && view === 'agenda' && (
        <AgendaView
          currentDate={currentDate}
          events={visibleEvents}
          daysAhead={settings.agendaDaysAhead}
          timeFormat={settings.timeFormat}
          onClickEvent={onClickEvent}
          agendaGrouping={settings.agendaGrouping}
          dimPastEvents={settings.dimPastEvents}
          eventColorPriority={settings.eventColorPriority}
        />
      )}
    </div>
  );
}
